import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CheckboxField, ChevronButton } from './swegon-compat/components';
import classNames from 'classnames';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from '../tailwind.config.cjs';

const tailwindResolvedConfig = resolveConfig(tailwindConfig);

export * from './swegon-compat/components';

export const CollapsiblePanel = (props) => {
    const {
        header: HeaderComponent,
        title,
        children,
        defaultOpen,
        bordered,
        showWarning,
    } = props;
    const [open, setOpen] = React.useState(defaultOpen ?? false);

    return (
        <div className={classNames({ 'border border-black-20 p-4': bordered })}>
            <HeaderComponent
                open={open}
                title={title}
                onClick={() => setOpen(!open)}
                showWarning={showWarning}
            />
            <div className={classNames({ hidden: !open })}>{children}</div>
        </div>
    );
};

export const CollapsiblePanelHeader = (props) => {
    const { open, title, onClick, showWarning } = props;

    return (
        <div className="flex flex-wrap justify-between items-center pb-2 mb-2 text-2xl font-bold border-b border-black-20">
            {showWarning && (
                <div className="flex flex-row">
                    <div>{title}</div>
                    <div className="ml-2 text-yellow-100">{'*'}</div>
                </div>
            )}
            {!showWarning && <div>{title}</div>}
            <ChevronButton
                open={open}
                onClick={onClick}
            />
        </div>
    );
};

export const useTabs = (tabs, contentFn) => {
    const [activeTabKey, setActiveTabKey] = React.useState(
        tabs.find((t) => t.isDefault)?.key,
    );

    const TabsContainer = (
        <div className="flex flex-wrap">
            {tabs.map((tab) => (
                <div
                    key={tab.key}
                    className={classNames(
                        activeTabKey === tab.key
                            ? 'swegon-like-tab selected'
                            : 'swegon-like-tab',
                    )}
                    onClick={() => setActiveTabKey(tab.key)}
                >
                    {tab.title}
                </div>
            ))}
        </div>
    );

    return [TabsContainer, contentFn(activeTabKey)];
};

const TabValueContext = React.createContext();
const SetTabValueContext = React.createContext();

const TabsContext = (props) => {
    const { defaultValue, children } = props;
    const [tabValue, setTabValue] = React.useState(defaultValue);

    return (
        <SetTabValueContext.Provider value={setTabValue}>
            <TabValueContext.Provider value={tabValue}>
                {children}
            </TabValueContext.Provider>
        </SetTabValueContext.Provider>
    );
};

const TabClassNamesContext = React.createContext();

const TabList = (props) => {
    const { tabClassNames, activeTabClassNames, children } = props;

    return (
        <TabClassNamesContext.Provider
            value={[tabClassNames, activeTabClassNames]}
        >
            <div className="flex flex-wrap flex-row">{children}</div>
        </TabClassNamesContext.Provider>
    );
};

const Tab = (props) => {
    const { label, value: propsValue } = props;

    const tabValue = React.useContext(TabValueContext);
    const setTabValue = React.useContext(SetTabValueContext);

    return (
        <div
            className={
                tabValue === propsValue
                    ? 'swegon-like-tab selected'
                    : 'swegon-like-tab'
            }
            onClick={() => setTabValue(propsValue)}
        >
            {label}
        </div>
    );
};

const TabPanel = (props) => {
    const { value: propsValue, children } = props;

    const tabValue = React.useContext(TabValueContext);

    return propsValue === tabValue ? children : null;
};

export const Tabs = {
    Context: TabsContext,
    List: TabList,
    Tab: Tab,
    Panel: TabPanel,
};

export const Pagination = ({
    currentPage,
    totalCount,
    itemsPerPage,
    onPageChange,
}) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (totalPages <= 1) return null;

    const handlePrevious = () => {
        if (currentPage > 1) onPageChange(currentPage - 1);
    };

    const handleNext = () => {
        if (currentPage < totalPages) onPageChange(currentPage + 1);
    };

    const getPageNumbers = () => {
        const maxVisiblePages = 5;
        const pages = [];
        let startPage = Math.max(
            1,
            currentPage - Math.floor(maxVisiblePages / 2),
        );
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <div className="flex items-center justify-center mt-4">
            <button
                className={`swegon-like-button white ${
                    currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={handlePrevious}
                disabled={currentPage === 1}
            >
                Previous
            </button>

            {pageNumbers[0] > 1 && (
                <>
                    <button
                        className="swegon-like-button white"
                        onClick={() => onPageChange(1)}
                    >
                        1
                    </button>
                    <span className="mx-1">...</span>
                </>
            )}

            {pageNumbers.map((page) => (
                <button
                    key={page}
                    className={`swegon-like-button white ${
                        currentPage === page
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                    }`}
                    onClick={() => onPageChange(page)}
                    disabled={currentPage === page}
                >
                    {page}
                </button>
            ))}

            {pageNumbers[pageNumbers.length - 1] < totalPages && (
                <>
                    <span className="mx-1">...</span>
                    <button
                        className="swegon-like-button white"
                        onClick={() => onPageChange(totalPages)}
                    >
                        {totalPages}
                    </button>
                </>
            )}

            <button
                className={`swegon-like-button white ${
                    currentPage === totalPages
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                }`}
                onClick={handleNext}
                disabled={currentPage === totalPages}
            >
                Next
            </button>
        </div>
    );
};

export const ToolTip = (props) => {
    const { anchor, place, children, noArrow, opsDisable, opPhase } = props;
    const [wrapperId] = useState(() => `tool-tip-${uuidv4()}`);
    return (
        <Fragment>
            <div id={wrapperId}>
                {anchor}
                <ReactTooltip
                    className={classNames(
                        '!text-black-75 !opacity-100 !bg-white !filter !shadow-swegon1 !rounded-none !z-10',
                    )}
                    classNameArrow={classNames('!border-2 !border-white')}
                    anchorSelect={`#${wrapperId}`}
                    place={place}
                    openOnClick="true"
                    noArrow={noArrow}
                    arrowColor={tailwindResolvedConfig.theme.colors.black[2]}
                    clickable="true"
                >
                    {children}
                </ReactTooltip>
            </div>
            {opsDisable !== undefined && opsDisable && (
                <ReactTooltip
                    anchorSelect={`#${wrapperId}`}
                    place="top-start"
                >
                    {'Unable to ' +
                        opPhase +
                        ' while document build in progress'}
                </ReactTooltip>
            )}
        </Fragment>
    );
};

export const ProjectTagsFilter = (props) => {
    const { tags, setFilteredTags, anchor } = props;

    //unique tag types to be displayed by project filter
    const uniqueEquipmentTypes = useMemo(
        () => [...new Set(tags?.map((tag) => tag.equipmentType))],
        [tags],
    );
    const uniqueRoomTypes = useMemo(
        () => [...new Set(tags?.map((tag) => tag.room))],
        [tags],
    );
    const uniqueValueTypes = useMemo(
        () => [...new Set(tags?.map((tag) => tag.value))],
        [tags],
    );
    const uniqueTradeTypes = useMemo(
        () => [...new Set(tags?.map((tag) => tag.trade ?? ''))],
        [tags],
    );

    //state of the project filter
    const [selectedEquipmentTypes, setSelectedEquipmentTypes] =
        useState(uniqueEquipmentTypes);
    const [selectedRoomTypes, setSelectedRoomTypes] = useState(uniqueRoomTypes);
    const [selectedValueTypes, setSelectedValueTypes] =
        useState(uniqueValueTypes);
    const [selectedTradeTypes, setSelectedTradeTypes] =
        useState(uniqueTradeTypes);

    //toggles what tags are selected
    const toggleEquipmentType = (equipmentType) => {
        if (selectedEquipmentTypes.includes(equipmentType)) {
            setSelectedEquipmentTypes(
                selectedEquipmentTypes.filter((type) => type !== equipmentType),
            );
        } else {
            setSelectedEquipmentTypes([
                ...selectedEquipmentTypes,
                equipmentType,
            ]);
        }
    };
    const toggleRoomType = (roomType) => {
        if (selectedRoomTypes.includes(roomType)) {
            setSelectedRoomTypes(
                selectedRoomTypes.filter((type) => type !== roomType),
            );
        } else {
            setSelectedRoomTypes([...selectedRoomTypes, roomType]);
        }
    };
    const toggleValueType = (valueType) => {
        if (selectedValueTypes.includes(valueType)) {
            setSelectedValueTypes(
                selectedValueTypes.filter((type) => type !== valueType),
            );
        } else {
            setSelectedValueTypes([...selectedValueTypes, valueType]);
        }
    };
    const toggleTradeType = (tradeType) => {
        if (selectedTradeTypes.includes(tradeType)) {
            setSelectedTradeTypes(
                selectedTradeTypes.filter((type) => type !== tradeType),
            );
        } else {
            setSelectedTradeTypes([...selectedTradeTypes, tradeType]);
        }
    };

    //filtered tags from parent is reloaded when selections change in this component
    useEffect(() => {
        setFilteredTags(
            tags?.filter(
                (tag) =>
                    selectedEquipmentTypes.includes(tag.equipmentType) &&
                    selectedRoomTypes.includes(tag.room) &&
                    selectedValueTypes.includes(tag.value) &&
                    selectedTradeTypes.includes(tag.trade ?? ''),
            ),
        );
    }, [
        selectedEquipmentTypes,
        selectedRoomTypes,
        selectedValueTypes,
        selectedTradeTypes,
        setFilteredTags,
        tags,
    ]);

    //when the tags change the selection of filters is reloaded
    useEffect(() => {
        setSelectedEquipmentTypes(uniqueEquipmentTypes);
        setSelectedRoomTypes(uniqueRoomTypes);
        setSelectedValueTypes(uniqueValueTypes);
        setSelectedTradeTypes(uniqueTradeTypes);
    }, [
        tags,
        uniqueEquipmentTypes,
        uniqueRoomTypes,
        uniqueValueTypes,
        uniqueTradeTypes,
    ]);

    return (
        <ToolTip
            anchor={anchor}
            place={'bottom-end'}
        >
            <div>
                <div>
                    <div className="top-0 left-0 mb-1">Equipment</div>
                    {uniqueEquipmentTypes.map((equipmentType) => (
                        <div
                            className="m-1"
                            key={equipmentType}
                        >
                            <CheckboxField
                                value={equipmentType}
                                title={equipmentType}
                                checked={selectedEquipmentTypes.includes(
                                    equipmentType,
                                )}
                                onChange={() =>
                                    toggleEquipmentType(equipmentType)
                                }
                            />
                        </div>
                    ))}
                </div>
                <div>
                    <div className="top-0 left-0 mb-1">Room/Location</div>
                    {uniqueRoomTypes.map((roomType) => (
                        <div
                            className="m-1"
                            key={roomType}
                        >
                            <CheckboxField
                                value={roomType}
                                title={roomType}
                                checked={selectedRoomTypes.includes(roomType)}
                                onChange={() => toggleRoomType(roomType)}
                            />
                        </div>
                    ))}
                </div>
                <div>
                    <div className="top-0 left-0 mb-1">Trade</div>
                    {uniqueTradeTypes.map((tradeType) => (
                        <div
                            className="m-1"
                            key={tradeType}
                        >
                            <CheckboxField
                                value={tradeType}
                                title={tradeType}
                                checked={selectedTradeTypes.includes(tradeType)}
                                onChange={() => toggleTradeType(tradeType)}
                            />
                        </div>
                    ))}
                </div>
                <div>
                    <div className="top-0 left-0 mb-1">Tags</div>
                    {uniqueValueTypes.map((valueType) => (
                        <div
                            className="m-1"
                            key={valueType}
                        >
                            <CheckboxField
                                value={valueType}
                                title={valueType}
                                checked={selectedValueTypes.includes(valueType)}
                                onChange={() => toggleValueType(valueType)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </ToolTip>
    );
};
